/**
 * @file stock-placement-payload.ts
 * @description Сбор тела `POST /admin/wms/transfers/placement` из шторки
 * размещения (phase19.24 этап 1, шаг 4).
 *
 * Логика вынесена из компонента, потому что ломается она незаметно: в режиме
 * «одна ячейка для всех» все строки обязаны получить один и тот же адрес, а в
 * режиме «по строкам» — каждая свой, и перепутанные местами ветки дают
 * документ, который проведётся и разложит товар не туда.
 *
 * Здесь же живут причины, по которым кнопка «Провести размещение» выключена:
 * текст причины показывается под кнопкой, а не молчаливым серым состоянием
 * (правило шапки документа §5.3 — у блокировки всегда есть фраза).
 *
 * Количество в строки не кладём: внутри склада партия переезжает целиком
 * (`partial_move_not_allowed`), и «весь остаток» — это отсутствие поля.
 */

import { PLACEMENT_TEXT } from "./stock-placement-texts";
import type { PlacementPayload } from "../../../lib/api-transfers";
import type { StockLevelDto } from "../../../lib/api-wms";

/** Как задаются ячейки: одна на всех или своя у каждой строки. */
export type PlacementMode = "shared" | "perRow";

/** Состояние шторки, из которого собирается документ. */
export interface PlacementDraft {
  mode: PlacementMode;
  /** Ячейка режима «одна для всех»; пустая строка — не выбрана. */
  sharedBinId: string;
  /** `id` строки остатка → ячейка режима «по строкам». */
  binByRow: Readonly<Record<string, string>>;
  rows: StockLevelDto[];
  note?: string;
}

/** Готовое тело запроса либо причина, по которой проводить нельзя. */
export interface PlacementBuildResult {
  payload: PlacementPayload | null;
  /** Причина блокировки кнопки; `null` — можно проводить. */
  hint: string | null;
}

/** Ячейка, выбранная для строки в текущем режиме. */
export function placementBinFor(draft: PlacementDraft, level: StockLevelDto): string {
  return draft.mode === "shared"
    ? draft.sharedBinId
    : (draft.binByRow[level.id] ?? "");
}

/** Строки с партией: беспартийному остатку адрес задать нечем. */
function placeableRows(rows: StockLevelDto[]): StockLevelDto[] {
  return rows.filter((level) => Boolean(level.lot));
}

export function buildPlacementPayload(draft: PlacementDraft): PlacementBuildResult {
  const rows = placeableRows(draft.rows);
  const [first] = rows;
  if (!first) {
    return { payload: null, hint: PLACEMENT_TEXT.emptySelection };
  }

  // Документ перемещения заводится по одному складу-источнику: строки с
  // разных складов сервер принять не сможет, и сказать это надо до отправки.
  const warehouseId = first.warehouseId;
  if (rows.some((level) => level.warehouseId !== warehouseId)) {
    return { payload: null, hint: PLACEMENT_TEXT.mixedWarehouses };
  }

  if (draft.mode === "shared" && !draft.sharedBinId) {
    return { payload: null, hint: PLACEMENT_TEXT.hintNeedSharedBin };
  }

  const missing = rows.filter((level) => !placementBinFor(draft, level)).length;
  if (missing > 0) {
    return { payload: null, hint: PLACEMENT_TEXT.hintNeedRowBins(missing) };
  }

  return {
    payload: {
      warehouseId,
      commit: true,
      ...(draft.note?.trim() ? { note: draft.note.trim() } : {}),
      items: rows.map((level) => ({
        // `lot` проверен в `placeableRows` — здесь он точно есть.
        lotId: level.lot!.id,
        toBinId: placementBinFor(draft, level),
      })),
    },
    hint: null,
  };
}
